import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  ChevronDown,
  Search,
  ArrowRight,
} from 'lucide-react';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import { faqs, FAQ_CATEGORIES } from '../../lib/faqData';
import '../../styles/Home.css';
import '../../styles/FaqPage.css';

export default function FaqPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [expandedIndices, setExpandedIndices] = useState([0]); // first item open by default

  const toggleAccordion = (index) => {
    if (expandedIndices.includes(index)) {
      setExpandedIndices(expandedIndices.filter((i) => i !== index));
    } else {
      setExpandedIndices([...expandedIndices, index]);
    }
  };

  const filteredFaqs = faqs.filter((faq) => {
    const matchCategory = activeCategory === 'All' || faq.category === activeCategory;
    const query = searchQuery.toLowerCase().trim();
    if (!query) return matchCategory;
    const matchQuestion = faq.question.toLowerCase().includes(query);
    const matchAnswer = faq.answer.toLowerCase().includes(query);
    const matchCat = faq.category.toLowerCase().includes(query);
    return matchCategory && (matchQuestion || matchAnswer || matchCat);
  });

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return (
    <div className="sx-faq-root">
      <SEO
        title="Frequently Asked Questions (FAQ) | SenritsuX - Music Distribution"
        description="Find answers to common questions about music distribution, royalties, master rights ownership, releases, and artist collaborations on SenritsuX."
        canonical="/faq"
        keywords={[
          'SenritsuX FAQ',
          'music distribution questions',
          'music royalties India',
          'keep 100 master rights',
          'Spotify distribution help',
          'artist release guide',
        ]}
        jsonLd={faqJsonLd}
      />
      <Navbar />

      <main className="sx-faq-main">
        {/* Hero Section */}
        <section className="sx-faq-hero" aria-label="Knowledge Base Header">
          <span className="sx-faq-badge">
            <HelpCircle size={14} aria-hidden="true" /> Knowledge Base
          </span>
          <h1 className="sx-faq-hero-title">
            Frequently Asked <span className="sx-faq-hero-highlight">Questions</span>
          </h1>
          <p className="sx-faq-hero-subtitle">
            Everything you need to know about releasing music globally, collecting royalties, managing releases, and collaborating on SenritsuX.
          </p>
        </section>

        {/* Search & Category Filter Section */}
        <section aria-label="Search and Category Filters">
          {/* Search Box */}
          <div className="sx-faq-search-wrapper">
            <input
              type="text"
              className="sx-faq-search-input"
              placeholder="Search by keyword (e.g. royalties, Spotify, master rights)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search FAQ questions"
            />
            <Search
              size={18}
              className="sx-faq-search-icon"
              aria-hidden="true"
            />
          </div>

          {/* Category Filter Pills */}
          <div
            className="sx-faq-categories-row"
            role="group"
            aria-label="Filter by category"
          >
            {FAQ_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                aria-pressed={activeCategory === cat}
                className={`sx-faq-cat-pill ${activeCategory === cat ? 'active' : ''}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* FAQ Accordion List */}
          <div className="sx-faq-list" role="region" aria-label="Questions list">
            {filteredFaqs.length === 0 ? (
              <div className="sx-faq-empty-card">
                <HelpCircle size={44} className="sx-faq-empty-icon" aria-hidden="true" />
                <h3 className="sx-faq-empty-title">
                  No matching questions found
                </h3>
                <p className="sx-faq-empty-desc">
                  Have a specific question that is not answered here? Reach out directly to our dedicated creator support team.
                </p>
                <Link
                  to="/contact"
                  className="sx-faq-empty-btn"
                >
                  Contact Support <ArrowRight size={16} />
                </Link>
              </div>
            ) : (
              filteredFaqs.map((item, idx) => {
                const isOpen = expandedIndices.includes(idx);
                return (
                  <div
                    key={item.slug}
                    className={`sx-faq-card ${isOpen ? 'is-open' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleAccordion(idx)}
                      aria-expanded={isOpen}
                      id={`faq-btn-${item.slug}`}
                      aria-controls={`faq-answer-${item.slug}`}
                      className="sx-faq-question-btn"
                    >
                      <span className="sx-faq-qtext">
                        {item.question}
                      </span>
                      <span className="sx-faq-chevron" aria-hidden="true">
                        <ChevronDown size={18} />
                      </span>
                    </button>

                    {isOpen && (
                      <div
                        id={`faq-answer-${item.slug}`}
                        role="region"
                        aria-labelledby={`faq-btn-${item.slug}`}
                        className="sx-faq-answer"
                      >
                        <p className="sx-faq-answer-p">
                          {item.answer}
                        </p>
                        <div className="sx-faq-meta-row">
                          <span className="sx-faq-cat-badge">
                            {item.category}
                          </span>
                          <Link
                            to={`/faq/${item.slug}`}
                            className="sx-faq-permalink"
                            aria-label={`Dedicated page for: ${item.question}`}
                          >
                            View full answer <ArrowRight size={13} />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Still Have Questions CTA */}
        <section className="sx-faq-cta-card" aria-label="Support Inquiry">
          <h2 className="sx-faq-cta-title">
            Still have questions?
          </h2>
          <p className="sx-faq-cta-desc">
            Our creator support team is ready to assist you with music distribution, metadata verification, royalty reports, and artist collaborations.
          </p>
          <Link
            to="/contact"
            className="sx-faq-cta-btn"
          >
            Contact Creator Support <ArrowRight size={18} />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
